import {ModalInstance} from "./ModalInstance";
import {IModalOptions, needToShowBackdrop} from "./IModalOptions";
import {ModalRenderer} from "./ModalRenderer";
import {ComponentRef, TemplateRef} from "@angular/core";
import {NModalBackdropComponent} from "./components/public_components";

const BODY_CLASS = "modal-open";

export class ModalStack {

  private stack: ModalInstance<any>[] = [];
  private backdrop: ComponentRef<NModalBackdropComponent>;
  private lastOpenedLayer: number;

  constructor() {

  }

  get openedModalCount(): number {
    return this.stack.length;
  }

  get openedVisibleModalCount(): number {
    return this.stack.filter(i => !i.stashed).length;
  }

  get openedBackdropModalCount(): number {
    return this.stack.filter(i => needToShowBackdrop(i.options)).length;
  }

  closeLast() {
    if (this.openedVisibleModalCount === 0) {
      return;
    }
    const instance = this.getLast();

    return instance.close();
  }

  getLast(): ModalInstance<any> {
    return this.stack[this.stack.length - 1];
  }

  open<TResult>(modalRenderer: ModalRenderer,
                modalData?: any,
                componentType?: Function,
                templateRef?: TemplateRef<any>,
                options?: IModalOptions): ModalInstance<TResult> {

    if (!componentType && !templateRef) {
      throw new Error("Specify template or component for modal");
    }

    const layer = this.openedModalCount > 0 ? this.lastOpenedLayer + 1 : 0;
    const instance = componentType ?
      modalRenderer.render<TResult>(componentType, modalData, options, layer) :
      modalRenderer.renderTemplate<TResult>(templateRef, modalData, options, layer);

    instance.dismiss.subscribe(async () => {
      await this.closeModalsAfter(instance);
    });

    this.stack.push(instance);
    this.lastOpenedLayer = layer;

    const onModalOpen = () => {
      if (needToShowBackdrop(options) && !this.backdrop) {
        this.backdrop = modalRenderer.renderBackdrop(options && options.injector);
      }

      if (options && options.openBodyClass) {
        addBodyClass(options.openBodyClass);
      }
      this.invalidateBodyClass();

      const last = this.getLast();
      if (this.stack.length > 1 && !last.options.dontMarkAsLast) {
        this.stack[this.stack.length - 2].markAsLast(false);
      }

      if (!last.options.dontMarkAsLast) {
        last.markAsLast(true);
      }
      last.focus();
    };

    if (!options || !options.stashed) {
      onModalOpen();
    }
    instance.onUnstash.subscribe(onModalOpen);

    return instance;
  }

  private async closeModalsAfter(instance: ModalInstance<any>) {
    const indx = this.stack.indexOf(instance);
    if (indx < 0) {
      return;
    }
    const moreModals = this.stack.slice(indx);
    for (let i = moreModals.length - 1; i >= 0; i--) {
      const modalClosed = await moreModals[i].close();
      if (modalClosed) {
        this.removeInstance(moreModals[i]);
      } else {
        break;
      }
    }
  }

  private invalidateBodyClass() {
    if (this.openedVisibleModalCount === 0) {
      removeBodyClass(BODY_CLASS);
    } else {
      addBodyClass(BODY_CLASS);
    }
  }

  private removeInstance(instance: ModalInstance<any>) {
    const indx = this.stack.indexOf(instance);
    if (indx < 0) {
      return;
    }

    this.stack.splice(indx, 1);

    if (instance.options.openBodyClass) {
      removeBodyClass(instance.options.openBodyClass);
    }

    this.invalidateBodyClass();

    if (this.openedBackdropModalCount === 0) {
      if (this.backdrop) {
        this.backdrop.destroy();
        this.backdrop = null;
      }
    } else {
      // focus last opened modal window
      const last = this.getLast();

      last.focus();
      last.markAsLast(true);
    }
  }
}

function addBodyClass(className: string) {
  document.body.classList.add(className);
}

function removeBodyClass(className: string) {
  document.body.classList.remove(className);
}
